<script lang="ts">
    import BackButton from "../shared/BackButton.svelte";
    import { gtag } from "../utils";

    gtag("set", "page_path", "/informacio");
    gtag("event", "page_view");
</script>

<svelte:head>
    <title>Roses UPC - Saber-ne més</title>
</svelte:head>

<BackButton fallbackPath="/" />

<h1>Com es generen les roses UPC?</h1>

<p>
Les Roses UPC 2023 es construeixen a partir de dues entrades
diferents: un esbós d’una figura i un text descriptiu en format lliure
introduïts per l’usuari. El resultat és un imatge amb una o més roses
que segueix la forma de l’esbós i intenta reflectir el text descriptiu.
<br><br>
Per generar aquesta imatge s’ha fet servir un model generatiu de deep
learning que consta d’una arquitectura de xarxes neuronals capaç de
produir una imatge a partir d’un text i un esbós. El model que s’ha
utilitzat per aquest Sant Jordi 2023 és <a href="https://github.com/lllyasviel/ControlNet" target="_blank"><b>ControlNet</b></a>: una xarxa neuronal
basada en el model text-a-imatge anomenat <a href="https://github.com/CompVis/stable-diffusion" target="_blank"><b>Stable Diffusion</b></a>.
<br><br>
<i>Stable Diffusion</i> és un model d’intel·ligència artificial dissenyat per
crear imatges a partir de text. És a dir, donant-li un text d’entrada, et
genera una imatge que coincideixi amb la descripció d’aquest text.
<br><br>
Per tal de guiar el procés de construcció de la imatge que es genera a
partir del text, s’ha fet servir <i>ControlNet</i>, una modificació de
l’arquitectura d’<i>Stable Diffusion</i> que permet condicionar com volem
que es generi la imatge a partir d’un esbós inicial fet per l’usuari. Així
doncs, amb <i>ControlNet</i> estem utilitzant esbossos i contorns per dir-li
exactament a <i>Stable Diffusion</i> com volem condicionar la composició
de la imatge a partir del text introduït.
<br><br>
A la figura següent es mostra com es generarien amb <i>ControlNet</i> dues
imatges diferents a partir d’una mateixa imatge (input) i dos textos
diferents: “a turtle in a river” i “a masterpiece of cartoon-style turtle
illustration”.
</p>

<img
    src="/img/tortuga-controlnet.png"
    alt="ScribbleDiffusion Turtle Demo"
    class="controlnet"
/>

<p>
    Enllaç ControlNet: <a href="https://arxiv.org/pdf/2302.05543.pdf" target="_blank" class="text-link">https://arxiv.org/pdf/2302.05543.pdf</a>
</p>

<p>
    Aquesta habilitat per dirigir a partir d’un text els models generatius cap
    al resultat que volem ha donat peu a una nova professió, la de <i>prompt
    engineer</i> (es podria traduir com a <i>enginyer de textos</i>, però fins i tot n'hi ha
    qui els anomenen <i>domadors d’intel·ligència artificial</i>).

    Per saber més coses de la <i>prompt engineering</i> podeu fer un cop d’ull al
    cercador d’imatges generades amb intel·ligència artificial anomenat
    Lexica (<a href="https://lexica.art/" target="_blank" class="text-link">https://lexica.art/</a>), que permet veure els textos que s’han fet
    servir per generar imatges amb models generatius.
</p>

<h2>Exemples</h2>

<div>
    <p>
        El teu esboç (imatge de l’esquerra) amb el text “Preciosa rosa brillant”
        es transforma en (imatge de la dreta)
    </p>
    <div class="imgs">
        <img
            class="simg"
            src="https://storage.googleapis.com/roses-inlab-2023/raw/f536b1a3-c326-4cc3-a1e1-677fd709b99d.png"
            alt="RosesUPC Preciosa Rosa Brillant Sketch"
        />
        <img
            class="simg"
            src="https://storage.googleapis.com/roses-inlab-2023/result/f536b1a3-c326-4cc3-a1e1-677fd709b99d.png"
            alt="RosesUPC Preciosa Rosa Brillant Resultat"
        />
    </div>
    <br /><br />
    <p>
        El teu esboç (imatge de l’esquerra) amb el text “Rosa badminton” es
        transforma en (imatge de la dreta)
    </p>
    <div class="imgs">
        <img
            class="simg"
            src="https://storage.googleapis.com/roses-inlab-2023/raw/d8c39d69-48ee-4954-a40a-1637a17ded2c.png"
            alt="RosesUPC Rosa Badminton Sketch"
        />
        <img
            class="simg"
            src="https://storage.googleapis.com/roses-inlab-2023/result/d8c39d69-48ee-4954-a40a-1637a17ded2c.png"
            alt="RosesUPC Rosa Badminton Resultat"
        />
    </div>
    <br />
</div>

<h2>
    T'interessen els estudis relacionats amb la informàtica i la intel·ligència
    artificial?
</h2>

<p>
    La UPC ofereix una estimulant oferta d’estudis de grau que tracten els
    diversos aspectes de la tecnologia que fan possible un projecte com Roses
    UPC.
</p>

<a
    href="https://www.upc.edu/ca/graus/"
    target="_blank"
    class="text-link large-link"
>
    Consulta la informació sobre aquests estudis i més Consulta la informació
    sobre els estudis de la UPC.
</a>

<h2>Sobre l'inLab FIB</h2>
<p>
    L’inLab FIB és el laboratori d’innovació i recerca de la Facultat
    d’Informàtica de Barcelona de la UPC. Està especialitzat en el
    desenvolupament de projectes R+D+i multidisciplinars a l'àmbit de les
    tecnologies de la informació, tant per a entitats públiques com privades.
</p>

<a
    href="https://inlab.fib.upc.edu/"
    target="_blank"
    class="text-link large-link"
>
    Per saber més sobre l'inLab FIB
</a>

<h2>Sobre UPCArts</h2>
<p>
    UPCArts és un programa d'activitats culturals per a tota la comunitat UPC
    que estableix ponts i aliances entre les ciències i la tecnologia amb l’art
    i la cultura. #RosaUPC23 ajuda a apropar la mirada artística i la
    tecnològica.
</p>

<a
    href="https://www.upc.edu/ca/upcarts"
    target="_blank"
    class="text-link large-link"
>
    Per saber més sobre UPCArts
</a>

<style>
    h1 {
        border-top: 3px solid var(--main-color);
        margin-top: 0;
        padding-top: 15px;
        color: var(--main-color);
        font-size: 2.5em;
        font-weight: 500;
    }

    p {
        color: black;
        text-align: justify;
    }

    b, i {
        color: var(--main-color);
    }

    .large-link {
        font-weight: 500;
        font-size: 1.17em;
    }
    .controlnet {
        width: 100%;
    }

    .imgs {
        display: flex;
    }
    .simg {
        width: 30%;
    }

    @media (max-width: 1000px) {
        #image-div {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
        }
    }
</style>
